import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { moneyCents } from '@/lib/format';
import { cn } from '@/lib/utils';
import { usePartnersAuth } from '@partners/auth/PartnersAuth';
import { Notice, Section } from '@partners/components/Shell';
import { QrImage, useQrSvg } from '@partners/components/QrImage';
import {
  dashboard,
  payoutRequest,
  PromoterRpcError,
  promoterErrorKey,
  terms,
  uploadW9,
  W9_MAX_BYTES,
  w9Submit,
  type Dashboard,
  type DashboardAgency,
  type DashboardCommission,
} from '@partners/data/promoter';
import { base64ToBytes } from '@partners/lib/bytes';
import { pdfObjectUrl } from '@partners/lib/pdfUrl';
import { formatDay, formatInZone, formatLocalDay, lastDayInZone } from '@partners/lib/zoneDates';

const IRS_W9_URL = 'https://www.irs.gov/pub/irs-pdf/fw9.pdf';
const usd = (cents: number) => moneyCents(cents / 100);

/** Errors that mean "the gate changed under you": back through promoter_whoami. */
const GATE_KEYS = new Set(['agreementRequired', 'programClosed', 'notAllowed']);

/**
 * The promoter's home (promoter_dashboard, design §9.3 screen 6). Mobile first: one column of cards; lists are
 * cards, never tables, so nothing scrolls sideways on a phone.
 */
/**
 * Reasons the SERVER writes (0271: the automatic suspension on a negative balance) are English constants; they
 * are said in the UI language. A reason an operator typed is shown as typed.
 */
const SYSTEM_REASONS: Record<string, string> = {
  'negative balance: paid money came back': 'promoter.home.suspendedNegativeBalance',
};

export function Home({ onGateChanged }: { onGateChanged: () => void }) {
  const { t } = useTranslation();
  const { client } = usePartnersAuth();
  const reasonText = (reason: string) => (SYSTEM_REASONS[reason] ? t(SYSTEM_REASONS[reason]) : reason);
  const [data, setData] = React.useState<Dashboard | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  const load = React.useCallback(async () => {
    try {
      setData(await dashboard(client));
      setError(null);
    } catch (err) {
      const key = promoterErrorKey(err);
      if (GATE_KEYS.has(key)) return onGateChanged();
      console.warn('promoter_dashboard failed', err);
      setError(t(`promoter.errors.${key}`));
    }
  }, [client, onGateChanged, t]);

  React.useEffect(() => {
    void load();
  }, [load]);

  if (error && !data) {
    return (
      <>
        <Notice>{error}</Notice>
        <Button variant="outline" onClick={() => void load()}>
          {t('promoter.common.retry')}
        </Button>
      </>
    );
  }
  if (!data) {
    return (
      <p className="text-ink-muted" role="status">
        {t('promoter.common.loading')}
      </p>
    );
  }

  return (
    <>
      <div>
        <h1 className="text-2xl font-semibold text-ink">{t('promoter.home.greeting', { name: data.promoter.display_name })}</h1>
        <p className="mt-1 text-sm text-ink-muted">
          {t('promoter.home.programLine', {
            percent: data.program.commission_percent,
            days: data.program.commission_days,
            hold: data.program.payout_hold_days,
          })}
        </p>
      </div>
      {data.promoter.status === 'suspended' && (
        <Notice tone="warning">
          {t('promoter.home.suspended')}
          {data.promoter.status_reason && <span className="block">{t('promoter.home.suspendedReason', { reason: reasonText(data.promoter.status_reason) })}</span>}
        </Notice>
      )}
      <Money data={data} onChanged={load} onGateChanged={onGateChanged} />
      <Share url={data.share.signup_url} code={data.share.code} />
      <Agencies agencies={data.agencies} />
      <Commissions rows={data.commissions} />
      <Payouts data={data} />
      <W9 data={data} onChanged={load} onGateChanged={onGateChanged} />
      <Documents docs={data.signed_documents} />
    </>
  );
}

function Money({ data, onChanged, onGateChanged }: { data: Dashboard; onChanged: () => Promise<void>; onGateChanged: () => void }) {
  const { t } = useTranslation();
  const { client } = usePartnersAuth();
  const [busy, setBusy] = React.useState(false);
  const [message, setMessage] = React.useState<{ tone: 'danger' | 'success'; text: string } | null>(null);
  const reasonId = React.useId();
  const reason = data.payout_blocked_reason ? t(`promoter.home.money.reasons.${data.payout_blocked_reason}`) : null;

  async function onRequest() {
    setBusy(true);
    setMessage(null);
    try {
      await payoutRequest(client);
      setMessage({ tone: 'success', text: t('promoter.home.money.requested') });
      await onChanged();
    } catch (err) {
      const key = promoterErrorKey(err);
      if (GATE_KEYS.has(key)) return onGateChanged();
      setMessage({ tone: 'danger', text: t(`promoter.errors.${key}`) });
    } finally {
      setBusy(false);
    }
  }

  const tiles: [string, number, string][] = [
    ['available', data.balance.available_cents, 'text-ink'],
    ['held', data.balance.held_cents, 'text-ink-secondary'],
    ['paid', data.balance.paid_cents, 'text-ink-secondary'],
  ];
  return (
    <Section id="money" title={t('promoter.home.money.title')}>
      <dl className="grid grid-cols-1 gap-3 min-[420px]:grid-cols-3">
        {tiles.map(([key, cents, tone]) => (
          <div key={key} className="rounded-md bg-sunken px-3 py-2">
            <dt className="text-sm text-ink-muted">{t(`promoter.home.money.${key}`)}</dt>
            <dd className={cn('text-xl font-semibold tabular-nums', tone)}>{usd(cents)}</dd>
          </div>
        ))}
      </dl>
      <div className="mt-4 flex flex-col gap-2">
        <Button
          onClick={() => void onRequest()}
          disabled={!data.can_request_payout || busy}
          aria-describedby={reason ? reasonId : undefined}
          className="w-full sm:w-auto sm:self-start"
        >
          {busy ? t('promoter.home.money.requesting') : t('promoter.home.money.request')}
        </Button>
        {reason && (
          <p id={reasonId} className="text-sm text-ink-muted">
            {reason}
          </p>
        )}
        {message && <Notice tone={message.tone}>{message.text}</Notice>}
      </div>
    </Section>
  );
}

function Share({ url, code }: { url: string; code: string }) {
  const { t } = useTranslation();
  const qr = useQrSvg(url);
  const [copied, setCopied] = React.useState(false);

  async function onCopy() {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.warn('copy failed', err);
    }
  }

  return (
    <Section id="share" title={t('promoter.home.share.title')}>
      <p className="mb-4 text-sm text-ink-secondary">{t('promoter.home.share.body')}</p>
      <div className="flex flex-col gap-4 min-[560px]:flex-row min-[560px]:items-start">
        <div className="flex min-w-0 flex-1 flex-col gap-3">
          <div>
            <p className="text-sm text-ink-muted">{t('promoter.home.share.code')}</p>
            <p className="font-mono text-lg font-semibold tracking-wider text-ink">{code}</p>
          </div>
          <div>
            <p className="text-sm text-ink-muted">{t('promoter.home.share.link')}</p>
            <p className="break-all text-base text-ink">{url}</p>
          </div>
          <Button variant="outline" onClick={() => void onCopy()} className="self-start">
            {copied ? t('promoter.common.copied') : t('promoter.common.copy')}
          </Button>
        </div>
        <div className="flex flex-col items-center gap-2">
          <QrImage src={qr} label={t('promoter.home.share.qr')} className="size-40 rounded-md border border-hairline bg-card p-1" />
          {qr && (
            <a href={qr} download={`coreway-${code}.svg`} className="text-sm font-medium text-accent-base hover:underline">
              {t('promoter.home.share.downloadQr')}
            </a>
          )}
        </div>
      </div>
    </Section>
  );
}

function Chip({ tone = 'neutral', children }: { tone?: 'neutral' | 'accent' | 'success' | 'warning' | 'danger'; children: React.ReactNode }) {
  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-xs font-medium',
        tone === 'neutral' && 'bg-sunken text-ink-secondary',
        tone === 'accent' && 'bg-accent-wash text-accent-base',
        tone === 'success' && 'bg-success/10 text-success',
        tone === 'warning' && 'bg-warning/10 text-ink',
        tone === 'danger' && 'bg-danger/10 text-danger',
      )}
    >
      {children}
    </span>
  );
}

const AGENCY_TONE = { not_paying: 'neutral', in_bar: 'accent', bar_done: 'success', paused_comp: 'warning' } as const;

function Agencies({ agencies }: { agencies: DashboardAgency[] }) {
  const { t, i18n } = useTranslation();
  const lng = i18n.resolvedLanguage ?? 'en';
  return (
    <Section id="agencies" title={t('promoter.home.agencies.title')}>
      {agencies.length === 0 ? (
        <p className="text-sm text-ink-muted">{t('promoter.home.agencies.empty')}</p>
      ) : (
        <ul className="flex flex-col gap-3">
          {agencies.map((a) => {
            const filled = Math.max(0, Math.min(a.filled_days ?? 0, a.commission_days));
            const pct = a.commission_days > 0 ? (filled / a.commission_days) * 100 : 0;
            const ended = a.state === 'bar_done';
            return (
              <li key={a.key} className="rounded-md border border-hairline p-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="min-w-0 font-medium text-ink">{a.name}</p>
                  <Chip tone={AGENCY_TONE[a.state] ?? 'neutral'}>{t(`promoter.home.agencies.state.${a.state}`)}</Chip>
                </div>
                <p className="mt-1 text-sm text-ink-muted">
                  {t('promoter.home.agencies.joined', { date: formatInZone(a.attached_at, a.time_zone, lng) })}
                  {' · '}
                  {t(`promoter.home.agencies.source.${a.source in AGENCY_SOURCES ? a.source : 'link'}`)}
                </p>
                {a.bar_starts_at && (
                  <div className="mt-3 flex flex-col gap-1.5">
                    <div
                      role="progressbar"
                      aria-label={t('promoter.home.agencies.barLabel', { name: a.name })}
                      aria-valuemin={0}
                      aria-valuemax={a.commission_days}
                      aria-valuenow={filled}
                      className="h-2 overflow-hidden rounded-full bg-sunken"
                    >
                      <div className={cn('h-full rounded-full', ended ? 'bg-success' : 'bg-accent-base')} style={{ width: `${pct}%` }} />
                    </div>
                    <p className="text-sm text-ink-secondary">
                      {t('promoter.home.agencies.progress', { filled: formatDays(filled), days: a.commission_days })}
                    </p>
                    <div className="flex flex-col gap-0.5 text-sm text-ink-muted min-[560px]:flex-row min-[560px]:gap-4">
                      <span>{t('promoter.home.agencies.barStart', { date: formatInZone(a.bar_starts_at, a.time_zone, lng) })}</span>
                      {a.bar_ends_at && (
                        <span>
                          {t(ended ? 'promoter.home.agencies.barEnded' : 'promoter.home.agencies.barEnd', {
                            date: lastDayInZone(a.bar_ends_at, a.time_zone, lng),
                          })}
                        </span>
                      )}
                      {(a.extension_days ?? 0) > 0 && <span>{t('promoter.home.agencies.extended', { days: formatDays(a.extension_days ?? 0) })}</span>}
                    </div>
                  </div>
                )}
                {!a.bar_starts_at && ended && a.bar_ends_at && (
                  <p className="mt-1 text-sm text-ink-muted">
                    {t('promoter.home.agencies.barEnded', { date: lastDayInZone(a.bar_ends_at, a.time_zone, lng) })}
                  </p>
                )}
                <p className="mt-2 text-sm text-ink-secondary">
                  {t('promoter.home.agencies.earned')}: <span className="font-medium tabular-nums text-ink">{usd(a.earned_cents)}</span>
                </p>
              </li>
            );
          })}
        </ul>
      )}
    </Section>
  );
}

const AGENCY_SOURCES = { link: 1, typed: 1, operator: 1 };
const formatDays = (d: number) => (Number.isInteger(d) ? String(d) : d.toFixed(1));

const COMMISSION_TONE = { held: 'neutral', available: 'accent', paid: 'success', reversed: 'danger' } as const;

function Commissions({ rows }: { rows: DashboardCommission[] }) {
  const { t, i18n } = useTranslation();
  const lng = i18n.resolvedLanguage ?? 'en';
  return (
    <Section id="commissions" title={t('promoter.home.commissions.title')}>
      {rows.length === 0 ? (
        <p className="text-sm text-ink-muted">{t('promoter.home.commissions.empty')}</p>
      ) : (
        <ul className="flex flex-col divide-y divide-hairline">
          {rows.map((c, i) => (
            <li key={`${c.agency_key}-${c.invoice_paid_at}-${i}`} className="flex flex-col gap-1 py-3 first:pt-0 last:pb-0">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="flex min-w-0 items-center gap-2">
                  <span className="font-medium text-ink">{t(`promoter.home.commissions.kind.${c.kind}`)}</span>
                  <Chip tone={COMMISSION_TONE[c.state] ?? 'neutral'}>{t(`promoter.home.commissions.state.${c.state}`)}</Chip>
                </span>
                <span className={cn('font-semibold tabular-nums', c.amount_cents < 0 ? 'text-danger' : 'text-ink')}>
                  {c.amount_cents < 0 ? `-${usd(-c.amount_cents)}` : usd(c.amount_cents)}
                </span>
              </div>
              {c.agency_name && <p className="text-sm text-ink-secondary">{c.agency_name}</p>}
              {c.kind !== 'commission' && c.adjusts_paid_at && (
                <p className="text-sm text-ink-secondary">
                  {t(c.kind === 'clawback' ? 'promoter.home.commissions.clawbackReason' : 'promoter.home.commissions.restoreReason', {
                    date: formatInZone(c.adjusts_paid_at, c.time_zone, lng),
                  })}
                </p>
              )}
              <p className="flex flex-col text-sm text-ink-muted min-[560px]:flex-row min-[560px]:gap-4">
                <span>{t('promoter.home.commissions.paidOn', { date: formatInZone(c.invoice_paid_at, c.time_zone, lng) })}</span>
                {c.kind === 'commission' && <span>{t('promoter.home.commissions.base', { amount: usd(Math.round(c.net_base_cents)) })}</span>}
                {c.state === 'held' && <span>{t('promoter.home.commissions.payableOn', { date: formatInZone(c.payable_at, c.time_zone, lng) })}</span>}
              </p>
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}

const METHODS = new Set(['ach', 'check', 'wire', 'zelle', 'other']);
const REQUEST_TONE = { pending: 'neutral', paid: 'success', rejected: 'danger' } as const;

function Payouts({ data }: { data: Dashboard }) {
  const { t, i18n } = useTranslation();
  const lng = i18n.resolvedLanguage ?? 'en';
  const empty = data.payouts.length === 0 && data.requests.length === 0;
  return (
    <Section id="payouts" title={t('promoter.home.payouts.title')}>
      {empty ? (
        <p className="text-sm text-ink-muted">{t('promoter.home.payouts.empty')}</p>
      ) : (
        <ul className="flex flex-col divide-y divide-hairline">
          {data.requests.map((r) => (
            <li key={`r-${r.requested_at}`} className="flex flex-col gap-1 py-3 first:pt-0 last:pb-0">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="flex items-center gap-2">
                  <span className="font-medium text-ink">{t('promoter.home.payouts.request')}</span>
                  <Chip tone={REQUEST_TONE[r.status]}>{t(`promoter.home.payouts.status.${r.status}`)}</Chip>
                </span>
                <span className="tabular-nums text-ink-secondary">{usd(r.available_cents_at_request)}</span>
              </div>
              <p className="text-sm text-ink-muted">{t('promoter.home.payouts.requestedOn', { date: formatLocalDay(r.requested_at, lng) })}</p>
              {r.status === 'rejected' && r.reject_reason && (
                <p className="text-sm text-ink-secondary">{t('promoter.home.payouts.rejected', { reason: r.reject_reason })}</p>
              )}
            </li>
          ))}
          {data.payouts.map((p) => (
            <li key={`p-${p.recorded_at}`} className="flex flex-col gap-1 py-3 first:pt-0 last:pb-0">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="font-medium text-ink">
                  {t('promoter.home.payouts.payout', { method: t(`promoter.home.payouts.method.${METHODS.has(p.method) ? p.method : 'other'}`) })}
                </span>
                <span className="font-semibold tabular-nums text-success">{usd(p.amount_cents)}</span>
              </div>
              <p className="flex flex-col text-sm text-ink-muted min-[560px]:flex-row min-[560px]:gap-4">
                <span>{t('promoter.home.payouts.paidOn', { date: formatDay(p.paid_on, lng) })}</span>
                {p.reference && <span>{t('promoter.home.payouts.reference', { reference: p.reference })}</span>}
              </p>
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}

const W9_TONE = { none: 'neutral', submitted: 'accent', accepted: 'success', rejected: 'danger' } as const;

function W9({ data, onChanged, onGateChanged }: { data: Dashboard; onChanged: () => Promise<void>; onGateChanged: () => void }) {
  const { t, i18n } = useTranslation();
  const lng = i18n.resolvedLanguage ?? 'en';
  const { client } = usePartnersAuth();
  const [busy, setBusy] = React.useState(false);
  const [message, setMessage] = React.useState<{ tone: 'danger' | 'success'; text: string } | null>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const w9 = data.w9;

  async function onUpload(file: File) {
    setMessage(null);
    const isPdf = file.type === 'application/pdf' || (file.type === '' && /\.pdf$/i.test(file.name));
    if (!isPdf) return setMessage({ tone: 'danger', text: t('promoter.errors.notPdf') });
    if (file.size < 1 || file.size > W9_MAX_BYTES) return setMessage({ tone: 'danger', text: t('promoter.errors.tooLarge') });
    setBusy(true);
    try {
      const objectName = await uploadW9(client, data.promoter.id, file);
      await w9Submit(client, objectName);
      setMessage({ tone: 'success', text: t('promoter.home.w9.uploaded') });
      await onChanged();
    } catch (err) {
      const key = err instanceof PromoterRpcError ? promoterErrorKey(err) : 'generic';
      if (GATE_KEYS.has(key)) return onGateChanged();
      console.warn('W-9 not submitted', err);
      setMessage({ tone: 'danger', text: t(`promoter.errors.${key}`) });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Section
      id="w9"
      title={t('promoter.home.w9.title')}
      action={<Chip tone={W9_TONE[w9.status] ?? 'neutral'}>{t(`promoter.home.w9.status.${w9.status}`)}</Chip>}
    >
      <p className="text-sm text-ink-secondary">{t('promoter.home.w9.body')}</p>
      <div className="mt-2 flex flex-col gap-0.5 text-sm text-ink-muted">
        {w9.status === 'submitted' && w9.submitted_at && <span>{t('promoter.home.w9.submittedOn', { date: formatLocalDay(w9.submitted_at, lng) })}</span>}
        {w9.status === 'accepted' && w9.accepted_at && <span>{t('promoter.home.w9.acceptedOn', { date: formatLocalDay(w9.accepted_at, lng) })}</span>}
        {w9.status === 'rejected' && w9.reject_reason && <span className="text-danger">{t('promoter.home.w9.rejectReason', { reason: w9.reject_reason })}</span>}
      </div>
      <a href={IRS_W9_URL} target="_blank" rel="noopener noreferrer" className="mt-3 inline-block text-sm font-medium text-accent-base hover:underline">
        {t('promoter.home.w9.blank')}
      </a>
      <div className="mt-4 flex flex-col gap-3">
        <input
          ref={inputRef}
          id="w9-file"
          type="file"
          accept="application/pdf,.pdf"
          aria-label={t('promoter.home.w9.choose')}
          className="sr-only"
          tabIndex={-1}
          onChange={(e) => {
            const chosen = e.target.files?.[0];
            // Cleared at once, so choosing the same file again (even a refused one) fires change again.
            e.target.value = '';
            if (chosen) void onUpload(chosen);
          }}
        />
        <p className="text-sm text-ink-muted">{w9.status === 'none' ? t('promoter.home.w9.choose') : t('promoter.home.w9.replace')}</p>
        <Button type="button" variant="outline" disabled={busy} className="self-start" onClick={() => inputRef.current?.click()}>
          {busy ? t('promoter.home.w9.uploading') : t('promoter.home.w9.upload')}
        </Button>
        {message && <Notice tone={message.tone}>{message.text}</Notice>}
      </div>
    </Section>
  );
}

function Documents({ docs }: { docs: Dashboard['signed_documents'] }) {
  const { t, i18n } = useTranslation();
  const lng = i18n.resolvedLanguage ?? 'en';
  const { client } = usePartnersAuth();
  const [error, setError] = React.useState<string | null>(null);

  async function onOpen(documentId: string) {
    setError(null);
    try {
      const doc = await terms(client, documentId);
      const url = pdfObjectUrl(base64ToBytes(doc.pdf_base64));
      if (url) window.open(url, '_blank', 'noopener');
    } catch (err) {
      console.warn('signed document not opened', err);
      setError(t(`promoter.errors.${promoterErrorKey(err)}`));
    }
  }

  return (
    <Section id="documents" title={t('promoter.home.documents.title')}>
      {docs.length === 0 ? (
        <p className="text-sm text-ink-muted">{t('promoter.home.documents.empty')}</p>
      ) : (
        <ul className="flex flex-col divide-y divide-hairline">
          {docs.map((d) => (
            <li key={d.document_id} className="flex flex-wrap items-center justify-between gap-2 py-3 first:pt-0 last:pb-0">
              <span className="min-w-0">
                <span className="block font-medium text-ink">{t('promoter.home.documents.item', { version: d.version })}</span>
                <span className="block text-sm text-ink-muted">{t('promoter.home.documents.signedOn', { date: formatLocalDay(d.signed_at, lng) })}</span>
              </span>
              <Button variant="outline" size="sm" onClick={() => void onOpen(d.document_id)}>
                {t('promoter.common.openPdf')}
              </Button>
            </li>
          ))}
        </ul>
      )}
      <Notice className={error ? 'mt-3' : undefined}>{error}</Notice>
    </Section>
  );
}
